import {useEffect, useMemo, useState} from 'react';
import {Layout} from '@/components/Layout';
import {Button} from '@/components/ui/button';
import {Card} from '@/components/ui/card';
import {Badge} from '@/components/ui/badge';
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {Textarea} from '@/components/ui/textarea';
import {toast} from 'sonner';
import {AlertTriangle, Edit2, Palette, Plus, Search, Shapes, Trash2} from 'lucide-react';
import {usePermissions} from '@/hooks/usePermissions';
import {
    api,
    getApiErrorMessage,
    objectColorEndpoints,
    objectIconEndpoints,
    occurrencePriorityEndpoints,
} from '@/lib/api';
import {PERMISSIONS} from '@/lib/permissions';

const emptyForm = {
    code: '',
    priority: '0',
    name: '',
    description: '',
    object_color_id: '',
    object_icon_id: '',
};

const collectionPayload = (payload, key) => {
    const source = payload?.data ?? payload;
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
    if (Array.isArray(source?.[key])) return source[key];
    return [];
};

const idFrom = (source) => source?.id ?? source?.object_color_id ?? source?.object_icon_id ?? '';

const normalizeOption = (source = {}) => {
    const id = idFrom(source);
    const label = source.label || source.name || source.title || String(id);
    return {
        ...source,
        id,
        name: label,
        label,
        color: source.color || source.hex || source.hex_color || source.value || '',
        icon: source.icon || source.icon_name || source.name || '',
    };
};

const ColorOption = ({color}) => (
    <span className="inline-flex items-center gap-2 min-w-0">
        <span
            className="w-3 h-3 rounded-full border border-slate-200 flex-shrink-0"
            style={{backgroundColor: color?.color || '#64748b'}}
        />
        <span className="truncate">{color?.label || color?.name || 'Sem nome'}</span>
    </span>
);

const IconOption = ({icon}) => (
    <span className="inline-flex items-center gap-2 min-w-0">
        {icon?.icon ? (
            <img src={icon.icon} alt="" className="w-4 h-4 object-contain flex-shrink-0"/>
        ) : (
            <Shapes size={14} className="text-slate-500 flex-shrink-0"/>
        )}
        <span className="truncate">{icon?.label || icon?.name || 'Sem nome'}</span>
    </span>
);

const numberFrom = (...values) => {
    const value = values.find(item => item !== undefined && item !== null);
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const normalizePriority = (source = {}, fallback = {}) => ({
    ...fallback,
    ...source,
    id: source.id ?? source.occurrence_priority_id ?? fallback.id,
    uuid: source.uuid ?? fallback.uuid,
    code: source.code ?? fallback.code ?? '',
    priority: numberFrom(source.priority, fallback.priority),
    name: source.name ?? fallback.name ?? '',
    description: source.description ?? fallback.description ?? '',
    object_color_id: source.object_color_id ?? source.objectColorId ?? fallback.object_color_id ?? null,
    object_icon_id: source.object_icon_id ?? source.objectIconId ?? fallback.object_icon_id ?? null,
});

const normalizePriorities = (payload) => {
    const priorities = collectionPayload(payload, 'priorities');
    const occurrencePriorities = priorities.length ? priorities : collectionPayload(payload, 'occurrence_priorities');
    return occurrencePriorities
        .map((priority, index) => normalizePriority(priority, {id: index + 1}))
        .sort((a, b) => Number(a.priority) - Number(b.priority) || String(a.name).localeCompare(String(b.name)));
};

const priorityPayload = (form) => ({
    code: form.code.trim(),
    priority: Number(form.priority) || 0,
    name: form.name.trim(),
    description: form.description.trim() || null,
    object_color_id: form.object_color_id ? Number(form.object_color_id) : null,
    object_icon_id: form.object_icon_id ? Number(form.object_icon_id) : null,
});

const SETTINGS_ACTION_PERMISSIONS = {
    create: PERMISSIONS.SETTINGS_CREATE,
    read: PERMISSIONS.SETTINGS_READ,
    update: PERMISSIONS.SETTINGS_UPDATE,
    delete: PERMISSIONS.SETTINGS_DELETE,
};

export const OccurrencePrioritiesPage = () => {
    const {can} = usePermissions();
    const [priorities, setPriorities] = useState([]);
    const [colors, setColors] = useState([]);
    const [icons, setIcons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingPriority, setEditingPriority] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);

    const canReadSettings = can(SETTINGS_ACTION_PERMISSIONS.read);
    const canCreateSettings = can(SETTINGS_ACTION_PERMISSIONS.create);
    const canUpdateSettings = can(SETTINGS_ACTION_PERMISSIONS.update);
    const canDeleteSettings = can(SETTINGS_ACTION_PERMISSIONS.delete);
    const showActions = canUpdateSettings || canDeleteSettings;

    useEffect(() => {
        let cancelled = false;

        const loadData = async () => {
            if (!canReadSettings) {
                setPriorities([]);
                setColors([]);
                setIcons([]);
                setError('Nao tem permissao para consultar os niveis de prioridade.');
                setLoading(false);
                return;
            }

            setLoading(true);
            setError('');

            try {
                const [prioritiesResponse, colorsResponse, iconsResponse] = await Promise.all([
                    api.get(occurrencePriorityEndpoints.list),
                    api.get(objectColorEndpoints.list).catch(() => ({data: []})),
                    api.get(objectIconEndpoints.list).catch(() => ({data: []})),
                ]);

                if (!cancelled) {
                    setPriorities(normalizePriorities(prioritiesResponse.data));
                    setColors(collectionPayload(colorsResponse.data, 'colors').map(normalizeOption).filter(option => option.id));
                    setIcons(collectionPayload(iconsResponse.data, 'icons').map(normalizeOption).filter(option => option.id));
                }
            } catch (requestError) {
                if (!cancelled) {
                    setPriorities([]);
                    setError(getApiErrorMessage(requestError, 'Nao foi possivel carregar os niveis de prioridade.'));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadData();

        return () => {
            cancelled = true;
        };
    }, [canReadSettings]);

    const colorById = useMemo(() => new Map(colors.map(color => [String(color.id), color])), [colors]);
    const iconById = useMemo(() => new Map(icons.map(icon => [String(icon.id), icon])), [icons]);

    const filteredPriorities = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return priorities;
        return priorities.filter(priority => [
            priority.code,
            priority.priority,
            priority.name,
            priority.description,
            priority.uuid,
        ].filter(value => value !== undefined && value !== null).some(value => String(value).toLowerCase().includes(q)));
    }, [priorities, search]);

    const openCreate = () => {
        if (!canCreateSettings) {
            toast.error('Nao tem permissao para criar niveis de prioridade.');
            return;
        }

        setEditingPriority(null);
        setForm(emptyForm);
        setDialogOpen(true);
    };

    const openEdit = (priority) => {
        if (!canUpdateSettings) {
            toast.error('Nao tem permissao para editar niveis de prioridade.');
            return;
        }

        setEditingPriority(priority);
        setForm({
            code: priority.code || '',
            priority: String(priority.priority ?? 0),
            name: priority.name || '',
            description: priority.description || '',
            object_color_id: priority.object_color_id ? String(priority.object_color_id) : '',
            object_icon_id: priority.object_icon_id ? String(priority.object_icon_id) : '',
        });
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (editingPriority && !canUpdateSettings) {
            toast.error('Nao tem permissao para editar niveis de prioridade.');
            return;
        }

        if (!editingPriority && !canCreateSettings) {
            toast.error('Nao tem permissao para criar niveis de prioridade.');
            return;
        }

        const code = form.code.trim();
        if (!code) {
            toast.error('Codigo e obrigatorio.');
            return;
        }

        if (!/^[a-z]+$/.test(code)) {
            toast.error('Codigo deve conter apenas letras minusculas.');
            return;
        }

        if (!form.name.trim()) {
            toast.error('Nome e obrigatorio.');
            return;
        }

        setSubmitting(true);
        try {
            if (editingPriority) {
                const response = await api.post(occurrencePriorityEndpoints.update, {
                    id: editingPriority.id,
                    uuid: editingPriority.uuid,
                    ...priorityPayload(form),
                });
                const updated = normalizePriority(response.data?.data ?? response.data?.priority ?? response.data?.occurrence_priority ?? response.data, {
                    ...editingPriority,
                    ...priorityPayload(form),
                });
                setPriorities(prev => prev.map(priority => priority.id === editingPriority.id ? updated : priority));
                toast.success(`Prioridade "${updated.name}" atualizada.`);
            } else {
                const response = await api.post(occurrencePriorityEndpoints.create, priorityPayload(form));
                const created = normalizePriority(response.data?.data ?? response.data?.priority ?? response.data?.occurrence_priority ?? response.data, {
                    id: Date.now(),
                    ...priorityPayload(form),
                });
                setPriorities(prev => [created, ...prev]);
                toast.success(`Prioridade "${created.name}" criada.`);
            }
            setDialogOpen(false);
        } catch (requestError) {
            toast.error(getApiErrorMessage(requestError, 'Nao foi possivel guardar o nivel de prioridade.'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (priority) => {
        if (!canDeleteSettings) {
            toast.error('Nao tem permissao para eliminar niveis de prioridade.');
            return;
        }

        if (!window.confirm(`Eliminar prioridade "${priority.name}"?`)) return;

        setSubmitting(true);
        try {
            await api.post(occurrencePriorityEndpoints.delete, {
                id: priority.id,
                uuid: priority.uuid,
            });
            setPriorities(prev => prev.filter(item => item.id !== priority.id));
            toast.success(`Prioridade "${priority.name}" removida.`);
        } catch (requestError) {
            toast.error(getApiErrorMessage(requestError, 'Nao foi possivel remover o nivel de prioridade.'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Niveis de Prioridade</h1>
                        <p className="text-slate-600 font-primary mt-1">Gerir niveis usados para classificar a prioridade das ocorrencias.</p>
                    </div>
                    {canCreateSettings && (
                        <Button onClick={openCreate} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="create-occurrence-priority">
                            <Plus size={18} className="mr-2"/>
                            Novo Nivel
                        </Button>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="p-4">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-slate-600 font-medium">Total</p>
                            <AlertTriangle size={18} className="text-blue-600"/>
                        </div>
                        <p className="text-2xl font-secondary font-bold text-slate-900 mt-1">{priorities.length}</p>
                    </Card>
                    <Card className="p-4">
                        <p className="text-sm text-slate-600 font-medium">Prioridade minima</p>
                        <p className="text-2xl font-secondary font-bold text-slate-900 mt-1">{priorities.length ? Math.min(...priorities.map(item => item.priority)) : '-'}</p>
                    </Card>
                    <Card className="p-4">
                        <p className="text-sm text-slate-600 font-medium">Prioridade maxima</p>
                        <p className="text-2xl font-secondary font-bold text-slate-900 mt-1">{priorities.length ? Math.max(...priorities.map(item => item.priority)) : '-'}</p>
                    </Card>
                </div>

                <div className="relative max-w-md">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                    <Input className="pl-9" value={search} onChange={event => setSearch(event.target.value)} placeholder="Pesquisar por codigo, prioridade, nome, descricao ..." data-testid="occurrence-priority-search"/>
                </div>

                <Card className="overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Codigo</TableHead>
                                <TableHead>Prioridade</TableHead>
                                <TableHead>Nome</TableHead>
                                <TableHead>Cor</TableHead>
                                <TableHead>Icone</TableHead>
                                {showActions && <TableHead className="text-right">Acoes</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(loading || error || filteredPriorities.length === 0) ? (
                                <TableRow>
                                    <TableCell colSpan={showActions ? 6 : 5} className="text-center text-sm text-slate-400 italic py-10">
                                        {loading ? 'A carregar niveis...' : error || 'Sem niveis para mostrar.'}
                                    </TableCell>
                                </TableRow>
                            ) : filteredPriorities.map(priority => {
                                const color = colorById.get(String(priority.object_color_id));
                                const icon = iconById.get(String(priority.object_icon_id));
                                return (
                                    <TableRow key={priority.id} data-testid={`occurrence-priority-row-${priority.id}`}>
                                        <TableCell>
                                            <code className="text-xs font-medium text-slate-700 bg-slate-100 px-2 py-1 rounded">
                                                {priority.code || '-'}
                                            </code>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="font-mono">{priority.priority}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <p className="font-medium text-slate-900">{priority.name}</p>
                                            {priority.description && <p className="text-xs text-slate-500 line-clamp-1">{priority.description}</p>}
                                        </TableCell>
                                        <TableCell>
                                            {color ? <ColorOption color={color}/> : <span className="text-xs text-slate-400 italic">Sem cor</span>}
                                        </TableCell>
                                        <TableCell>
                                            {icon ? <IconOption icon={icon}/> : <span className="text-xs text-slate-400 italic">Sem icone</span>}
                                        </TableCell>
                                        {showActions && (
                                            <TableCell className="text-right">
                                                {canUpdateSettings && (
                                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(priority)} disabled={submitting} data-testid={`edit-occurrence-priority-${priority.id}`}>
                                                        <Edit2 size={14} className="text-slate-600"/>
                                                    </Button>
                                                )}
                                                {canDeleteSettings && (
                                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleDelete(priority)} disabled={submitting} data-testid={`delete-occurrence-priority-${priority.id}`}>
                                                        <Trash2 size={14} className="text-red-500"/>
                                                    </Button>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Card>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-xl" data-testid="occurrence-priority-dialog">
                        <DialogHeader>
                            <DialogTitle>{editingPriority ? 'Editar Nivel de Prioridade' : 'Novo Nivel de Prioridade'}</DialogTitle>
                            <DialogDescription>Configurar codigo, prioridade, nome, descricao, cor e icone.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-2">
                            <div className="grid grid-cols-3 gap-3">
                                <div className="space-y-2 col-span-2">
                                    <Label>Codigo *</Label>
                                    <Input
                                        value={form.code}
                                        onChange={event => setForm(prev => ({...prev, code: event.target.value.toLowerCase()}))}
                                        pattern="[a-z]+"
                                        data-testid="occurrence-priority-code"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Prioridade *</Label>
                                    <Input type="number" min="0" value={form.priority} onChange={event => setForm(prev => ({...prev, priority: event.target.value}))} data-testid="occurrence-priority-value"/>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Nome *</Label>
                                <Input value={form.name} onChange={event => setForm(prev => ({...prev, name: event.target.value}))} data-testid="occurrence-priority-name"/>
                            </div>
                            <div className="space-y-2">
                                <Label>Descricao</Label>
                                <Textarea rows={3} value={form.description} onChange={event => setForm(prev => ({...prev, description: event.target.value}))} data-testid="occurrence-priority-description"/>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-1"><Palette size={13}/> Cor</Label>
                                    <Select value={form.object_color_id || 'none'} onValueChange={value => setForm(prev => ({...prev, object_color_id: value === 'none' ? '' : value}))}>
                                        <SelectTrigger data-testid="occurrence-priority-color">
                                            <SelectValue>
                                                {form.object_color_id ? <ColorOption color={colorById.get(String(form.object_color_id))}/> : 'Sem cor'}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Sem cor</SelectItem>
                                            {colors.map(color => (
                                                <SelectItem key={color.id} value={String(color.id)}>
                                                    <ColorOption color={color}/>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-1"><Shapes size={13}/> Icone</Label>
                                    <Select value={form.object_icon_id || 'none'} onValueChange={value => setForm(prev => ({...prev, object_icon_id: value === 'none' ? '' : value}))}>
                                        <SelectTrigger data-testid="occurrence-priority-icon">
                                            <SelectValue>
                                                {form.object_icon_id ? <IconOption icon={iconById.get(String(form.object_icon_id))}/> : 'Sem icone'}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Sem icone</SelectItem>
                                            {icons.map(icon => (
                                                <SelectItem key={icon.id} value={String(icon.id)}>
                                                    <IconOption icon={icon}/>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>Cancelar</Button>
                            <Button onClick={handleSubmit} disabled={submitting || (editingPriority ? !canUpdateSettings : !canCreateSettings)} className="bg-blue-600 hover:bg-blue-700" data-testid="occurrence-priority-submit">
                                {submitting ? 'A guardar...' : editingPriority ? 'Guardar' : 'Criar Nivel'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};
